import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import React from "react";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AppProvider } from "@/contexts/AppContext";
import { CartProvider } from "@/contexts/CartContext";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import ScrollToTop from "@/components/ScrollToTop";
import AmbientPlayer from "@/components/AmbientPlayer";
import AIChat from "@/components/AIChat";

import HomePage from "@/pages/HomePage";
import CatalogPage from "@/pages/CatalogPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import ArtistsPage from "@/pages/ArtistsPage";
import ArtistDetailPage from "@/pages/ArtistDetailPage";
import CheckoutPage from "@/pages/CheckoutPage";
import MyOrdersPage from "@/pages/MyOrdersPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ProfilePage from "@/pages/ProfilePage";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminArtists from "@/pages/admin/AdminArtists";
import AdminContent from "@/pages/admin/AdminContent";
import AdminBuckets from "@/pages/admin/AdminBuckets";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminLiveCarts from "@/pages/admin/AdminLiveCarts";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminDiscounts from "@/pages/admin/AdminDiscounts";
import AdminGiftCards from "@/pages/admin/AdminGiftCards";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminDelivery from "@/pages/admin/AdminDelivery";
import AdminVault from "@/pages/admin/AdminVault";
import AdminWheel from "@/pages/admin/AdminWheel";
import AdminSpins from "@/pages/admin/AdminSpins";
import WheelPage from "@/pages/WheelPage";
import FavoritesPage from "@/pages/FavoritesPage";
import AboutPage from "@/pages/AboutPage";
import BlogPage from "@/pages/BlogPage";
import PrivacyPage from "@/pages/PrivacyPage";
import GiftCardsPage from "@/pages/GiftCardsPage";
import MyGiftCardsPage from "@/pages/MyGiftCardsPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentErrorPage from "@/pages/PaymentErrorPage";
import PaymentResultPage from "@/pages/PaymentResultPage";

function StoreLayout({ children }) {
  return (
    <>
      <Header />
      <main>{children}</main>
      <CartDrawer />
      <AmbientPlayer />
      <AIChat />
      <Footer />
    </>
  );
}

function AdminGuard({ children }) {
  const { authChecked, user, isAdmin } = useAuth();
  if (!authChecked) return <div className="min-h-screen flex items-center justify-center text-[var(--text-2)]">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (!isAdmin) return <Navigate to="/profile" replace />;
  return children;
}

function AppShell() {
  return (
    <>
      <ScrollToTop />
      <Routes>
      <Route path="/" element={<StoreLayout><HomePage /></StoreLayout>} />
      <Route path="/catalog" element={<StoreLayout><CatalogPage /></StoreLayout>} />
      <Route path="/product/:slug" element={<StoreLayout><ProductDetailPage /></StoreLayout>} />
      <Route path="/artists" element={<StoreLayout><ArtistsPage /></StoreLayout>} />
      <Route path="/artist/:slug" element={<StoreLayout><ArtistDetailPage /></StoreLayout>} />
      <Route path="/checkout" element={<StoreLayout><CheckoutPage /></StoreLayout>} />
      <Route path="/checkout" element={<StoreLayout><CheckoutPage /></StoreLayout>} />
      <Route path="/orders" element={<StoreLayout><MyOrdersPage /></StoreLayout>} />
      <Route path="/orders/:tracking_no" element={<StoreLayout><MyOrdersPage /></StoreLayout>} />
      <Route path="/login" element={<StoreLayout><LoginPage /></StoreLayout>} />
      <Route path="/register" element={<StoreLayout><RegisterPage /></StoreLayout>} />
      <Route path="/profile" element={<StoreLayout><ProfilePage /></StoreLayout>} />
      <Route path="/favorites" element={<StoreLayout><FavoritesPage /></StoreLayout>} />
      <Route path="/about" element={<StoreLayout><AboutPage /></StoreLayout>} />
      <Route path="/blog" element={<StoreLayout><BlogPage /></StoreLayout>} />
      <Route path="/blog/:slug" element={<StoreLayout><BlogPage /></StoreLayout>} />
      <Route path="/privacy" element={<StoreLayout><PrivacyPage /></StoreLayout>} />
      <Route path="/gift-cards" element={<StoreLayout><GiftCardsPage /></StoreLayout>} />
      <Route path="/my-gift-cards" element={<StoreLayout><MyGiftCardsPage /></StoreLayout>} />
      <Route path="/wheel" element={<StoreLayout><WheelPage /></StoreLayout>} />

      {/* Epoint.az return URLs */}
      <Route path="/success" element={<StoreLayout><PaymentSuccessPage /></StoreLayout>} />
      <Route path="/succeess" element={<StoreLayout><PaymentSuccessPage /></StoreLayout>} />
      <Route path="/error" element={<StoreLayout><PaymentErrorPage /></StoreLayout>} />
      <Route path="/result" element={<PaymentResultPage />} />

      <Route path="/admin" element={<AdminGuard><AdminLayout /></AdminGuard>}>
        <Route index element={<AdminDashboard />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="categories" element={<AdminCategories />} />
        <Route path="artists" element={<AdminArtists />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="carts" element={<AdminLiveCarts />} />
        <Route path="notifications" element={<AdminNotifications />} />
        <Route path="coupons" element={<AdminCoupons />} />
        <Route path="discounts" element={<AdminDiscounts />} />
        <Route path="gift-cards" element={<AdminGiftCards />} />
        <Route path="delivery" element={<AdminDelivery />} />
        <Route path="content" element={<AdminContent />} />
        <Route path="blog" element={<AdminBlog />} />
        <Route path="buckets" element={<AdminBuckets />} />
        <Route path="wheel" element={<AdminWheel />} />
        <Route path="spins" element={<AdminSpins />} />
        <Route path="vault" element={<AdminVault />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppProvider>
            <CartProvider>
              <AppShell />
            </CartProvider>
          </AppProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
